import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ITeam } from '@core/interfaces/team.interface';
import { IObjectPermission } from '@core/interfaces/user.interface';
import { ECurrency, EOverwriteStatus, EPlatformFeature } from '@enums/campaign.enum';
import { ICreator } from '@pages/creators/creators.interface';
import { ICustomer } from '@pages/customers/customers.interface';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { ICtaCreator, TCtaTemplateCreatePayload } from './form-fields/creators-and-ctas/cta-dialog.interface';
import { TDeliverableForm } from './form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { IExecutionForm } from './form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';

export interface ICampaignForm {
  budget: FormControl<string | null>;
  currency: FormControl<ECurrency | null>;
  customer: FormControl<ICustomer | null>;
  customerId: FormControl<string | null>;
  endDate: FormControl<Date | null>;
  endTime: FormControl<Date | null>;
  name: FormControl<string | null>;
  notes: FormControl<string | null>;
  startDate: FormControl<Date | null>;
  startTime: FormControl<Date | null>;
  /** The campaign's own organizational team assignment - independent of `whitelist` (who can
   * *see* the campaign). Used purely to power the campaigns list's "Team" filter (see
   * CampaignTeamSelectComponent's doc comment). */
  team: FormControl<ITeam | null>;
  teamId: FormControl<string | null>;
  whitelist: FormControl<IObjectPermission[] | null>;
}

export interface IIconForm {
  icon: FormControl<string | null>;
  imageFile: FormControl<ICustomFile | null>;
}

// Same shape as IIconForm, for consumers of the renamed `icon` -> `iconId` column (customers, users).
// Creators kept their `icon` column/legacy polymorphic custom-value support as-is (see
// FILE_VALUE_NAME_MARKERS / creators.icon on the backend), so IIconForm above stays unrenamed and is
// still used exclusively by creator-details/creator-details-banner.
export interface IIconIdForm {
  iconId: FormControl<string | null>;
  imageFile: FormControl<ICustomFile | null>;
}

export interface ICampaignCreatorForm {
  cost: FormControl<string | null>;
  creator: FormControl<ICreator | null>;
  creatorId: FormControl<string | null>;
  campaignId: FormControl<string | null>;
  id: FormControl<string | null>;
  ctas: FormArray<FormGroup<ICtaForm>>;
}

export interface ICtaForm extends ICustomCtaForm {
  id: FormControl<string | null>;
  isCtaDetailsValid: FormControl<boolean | null>;
  name: FormControl<string | null>;
  icon: FormControl<string | null>;
  platformFeature: FormControl<EPlatformFeature>;
  deliverables: FormArray<FormGroup<TDeliverableForm>>;
  executions: FormArray<FormGroup<IExecutionForm>>;
  isAllCreators: FormControl<boolean | null>;
  isCustomMode: FormControl<boolean | null>;
}

export interface ICustomCtaForm {
  customCtaValues?: FormControl<ICustomCtaValue[]>;
  exclusiveTo?: FormControl<string | null | undefined>;
  globalCtaValues?: FormControl<IGlobalCtaValue[] | null>;
}

export interface ICustomCta {
  customCtaValues?: ICustomCtaValue[];
  exclusiveTo?: string | null;
  globalCtaValues?: IGlobalCtaValue[];
}

export interface IGlobalCtaValue {
  valueName: string;
  value: string | null;
}

export interface ICustomCtaValue {
  id: string;
  valueName: string | null;
  value: string | null;
  status: EOverwriteStatus;
  creatorId: string;
}

export type TUniqueCtaTemplates = FormGroup<ICtaForm>['value'] & { ctaCreators: ICtaCreator[] };

export interface IGlobalTrackingForm {
  id: FormControl<string | null>;
  campaignId: FormControl<string | null>;
  version: FormControl<number | null>;
  trackings: FormArray<FormGroup<TDeliverableForm>>;
}

export interface INavigationState {
  adminMode?: boolean;
  fromCampaignDetails?: boolean;
  openBasicsOnLoad?: boolean;
}

export enum ECampaignFormSection {
  basics = 'basics',
  creatorsAndCtas = 'creatorsAndCtas',
  keywordTracking = 'keywordTracking',
}

export interface ICampaignFormSubmitInfo {
  type: ECampaignFormSubmitType;
  section: ECampaignFormSection;
  mappedCtaTemplates?: TCtaTemplateCreatePayload[];
}

export enum ECampaignFormSubmitType {
  draft = 'draft',
  start = 'start',
}
